import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import app from '../../firebase';
import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';

const RENDER_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendRenderMediaStoryVideo';

const safeSegment = (value, fallback = 'guide') => String(value || fallback)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9_-]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 100) || fallback;

const extensionForFile = (file, sourceKind = 'video') => {
  const namedExtension = String(file?.name || '').match(/\.([a-zA-Z0-9]{2,5})$/)?.[1];
  if (namedExtension) return namedExtension.toLowerCase();
  const mimeExtension = String(file?.type || '').split('/')[1]?.split(';')[0];
  if (mimeExtension === 'quicktime') return 'mov';
  return safeSegment(mimeExtension, sourceKind === 'image' ? 'jpg' : 'mp4');
};

const uploadWithProgress = (storageRef, blob, metadata, onProgress, range) => new Promise((resolve, reject) => {
  const task = uploadBytesResumable(storageRef, blob, metadata);
  task.on('state_changed', (snapshot) => {
    const ratio = snapshot.totalBytes ? snapshot.bytesTransferred / snapshot.totalBytes : 0;
    onProgress?.(Math.round(range[0] + ((range[1] - range[0]) * ratio)));
  }, reject, () => resolve(task.snapshot.ref.fullPath));
});

const uploadInputs = async ({
  guideKey,
  workspaceId,
  sourceKey,
  sourceFile,
  sourceKind,
  overlayBlob,
  existingSourcePath,
  sourceFingerprint,
  storedSourceFingerprint,
  onProgress,
}) => {
  const user = getAuth(app).currentUser;
  if (!user) throw new Error('Sign in before rendering a Story video.');
  const storage = getStorage(app);
  const root = `media_packages/${safeSegment(workspaceId || guideKey)}/render_inputs/${safeSegment(sourceKey, 'default')}`;
  let sourceMediaPath = existingSourcePath;

  if (!sourceMediaPath || (sourceFile && sourceFingerprint !== storedSourceFingerprint)) {
    if (!sourceFile) throw new Error('The saved Story source is unavailable. Choose the video again and retry.');
    const sourceRef = ref(storage, `${root}/source_${sourceKind}.${extensionForFile(sourceFile, sourceKind)}`);
    sourceMediaPath = await uploadWithProgress(
      sourceRef,
      sourceFile,
      { contentType: sourceFile.type || (sourceKind === 'image' ? 'image/jpeg' : 'video/mp4') },
      onProgress,
      [2, 48]
    );
  } else {
    onProgress?.(48);
  }

  const overlayRef = ref(storage, `${root}/story_overlay.png`);
  const overlayImagePath = await uploadWithProgress(
    overlayRef,
    overlayBlob,
    { contentType: 'image/png' },
    onProgress,
    [48, 58]
  );

  return { sourceMediaPath, overlayImagePath };
};

const render = async ({
  sourceMediaPath,
  sourceKind,
  workspaceId,
  overlayImagePath,
  audioUrl,
  guideKey,
  outputFilename,
  durationSeconds,
  kenBurnsZoom,
  kenBurnsDirection,
  colourWash,
  onProgress,
}) => {
  onProgress?.(60);
  const token = await getFirebaseAuthToken();
  const renderStartedAt = Date.now();
  const response = await fetch(RENDER_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sourceMediaPath,
        sourceKind,
        workspaceId,
        overlayImagePath,
        audioUrl: audioUrl || '',
        guideKey,
        outputFilename,
        durationSeconds,
        kenBurnsZoom,
        kenBurnsDirection,
        colourWash,
      }),
    }).catch((error) => {
      if (Date.now() - renderStartedAt >= 5 * 60 * 1000) {
        throw new Error('The Story render exceeded the server time limit. Try a shorter narration or contact support.');
      }
      throw error;
    });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const timedOut = response.status === 500 || response.status === 504;
    throw new Error(body.error || body.message || (timedOut
      ? 'The video render exceeded the server time limit. Try a shorter narration or render again.'
      : `Video render failed (${response.status}).`));
  }
  onProgress?.(100);
  return body;
};

export const mediaVideoRenderService = {
  uploadInputs,
  render,
};

export default mediaVideoRenderService;
